import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { getNextSignupRoute } from '@pages/Signup/routes/getNextSignupRoute'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'

import { isSumsubNeeded } from '@utils/AccountAccess/accountKYCStatuses'
import { hasSubmittedEconomicProfileTest } from '@utils/AccountAccess/accountProductStatuses'

import { Box } from '@shared/components/Box'
import { TestStatusTemplateInnerWrapper } from '@shared/templates/TestStatusTemplate/TestStatusTemplate'

import { SignupFormWrapper } from '../../Components/SignupFormWrapper'
import { SignupSections, SignupStep } from '../../Components/SignupStep'

export const FinancialInfoSuccessPage: React.FC = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()

  const isEconomicProfile = hasSubmittedEconomicProfileTest(account, product)
  const section = isEconomicProfile ? SignupSections.EconomicProfile : SignupSections.FinancialInfo

  const nextRoute = getNextSignupRoute(account)

  const content = useMemo(() => {
    if (isEconomicProfile) {
      return {
        title: t('Sign up.Economic Profile Complete'),
        text: [
          <>
            {t("Sign up.We've emailed all of the relevant information to")}{' '}
            <b>{account?.emails[0]?.address}</b>
          </>,
          t("Sign up.Let's get on to the next step – uploading your identification documents."),
        ],
      }
    }

    return {
      title: t('Sign up.Trading Experience Test Complete'),
      text: [
        <>
          {t("Sign up.We've emailed all of the relevant information to")}{' '}
          <b>{account?.emails[0]?.address}</b>
        </>,
        t("Sign up.Let's get on to the next step – uploading your identification documents."),
      ],
    }
  }, [account?.emails, isEconomicProfile, t])

  return (
    <SignupFormWrapper label={t('Sign up.Sign up')}>
      <TestStatusTemplateInnerWrapper>
        <SignupStep accountTypeId={account?.type.id} section={section} isCompleted />
        <Box flex fullWidth direction='column' gap='x32' align='center'>
          <ContentBlock
            variant='voucher'
            icon={
              <FeatureIcon
                size='lg'
                icon={CircleCheckIcon}
                bg='surface-success-high-em'
                color='text-white'
              />
            }
            title={content.title}
            description={content.text}
          />

          <Button
            size='lg'
            type='submit'
            iconRight={ArrowIcon.Right}
            onClick={() => navigate(nextRoute)}
          >
            {isSumsubNeeded(account) ? t('Sign up.Start Verification') : t('Next')}
          </Button>
        </Box>
      </TestStatusTemplateInnerWrapper>
    </SignupFormWrapper>
  )
}
